import { Icon, Page } from '@/components';
import { useGetHomeDetailsQuery, useGetHomeIdQuery } from '@/redux';
import {
  PersonalizationSummaryTabContent,
  TrackPersonalizationActivityModal,
  useTrackPersonalizationActivityModal,
} from '@/views';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styles from './PersonalizationSummaryPublicPage.module.scss';
import { useMemo } from 'react';

type ParsedId =
  | { type: 'guid'; id: string }
  | {
      type: 'composite';
      communityKey: string;
      homeNumber: string;
    };

const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// communityKey-homeNumber
// Examples:
// VSIA-001
// VSIA-001S
// VSIA-001S-1
const COMPOSITE_ID_REGEX = /^([^-]+)-(.+)$/;

export const useId = (): ParsedId => {
  const { id } = useParams<'id'>();

  if (!id) {
    throw new Error('Route param "id" is missing');
  }

  if (GUID_REGEX.test(id)) {
    return { type: 'guid', id };
  }

  const match = COMPOSITE_ID_REGEX.exec(id);

  if (match) {
    return {
      type: 'composite',
      communityKey: match[1],
      homeNumber: match[2],
    };
  }

  throw new Error(
    `Invalid id format. Expected GUID or <communityKey>-<homeNumber>, got "${id}"`,
  );
};

function _PersonalizationSummaryPublicPage() {
  const { t } = useTranslation();
  const idData = useId();

  const { data: homeId } = useGetHomeIdQuery(
    idData.type === 'composite'
      ? {
          communityKey: idData.communityKey,
          homeNumber: idData.homeNumber,
          allowAnonymous: true,
        }
      : { communityKey: '', homeNumber: '' },
    {
      skip: idData.type === 'guid',
    },
  );

  const resolvedHomeId = useMemo(() => {
    if (idData.type === 'guid') return idData.id;
    return homeId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idData.type, idData.type === 'guid' ? idData.id : undefined, homeId]);

  const { data } = useGetHomeDetailsQuery(
    { id: resolvedHomeId ?? '', allowAnonymous: true },
    { skip: !resolvedHomeId },
  );

  const activityModal = useTrackPersonalizationActivityModal(resolvedHomeId);

  if (!resolvedHomeId || !data) return null;

  const title = data.homeNumber
    ? `${data.homeNumber} - ${t('personalization.summary.title')}`
    : t('fetchFallbackTitle');

  return (
    <>
      <Page htmlTitle={title}>
        <Page.Body
          xs={{ span: 22, offset: 1 }}
          lg={{ span: 22, offset: 1 }}
          xl={{ span: 20, offset: 1 }}
          xxl={{ span: 18, offset: 3 }}
        >
          <Page.Card
            title={
              <Typography.Text className={styles.title}>
                {title}
              </Typography.Text>
            }
            icon={<Icon type="home" />}
            color="#107CC7"
          >
            <PersonalizationSummaryTabContent
              homeId={resolvedHomeId}
              personalizationHistory={data.personalizationHistory}
              communityId={data.communityId}
              isPublic
            />
          </Page.Card>
        </Page.Body>
      </Page>

      <TrackPersonalizationActivityModal {...activityModal} />
    </>
  );
}

export const PersonalizationSummaryPublicPage =
  _PersonalizationSummaryPublicPage;
